/**
 * LiveEditor ↔ neue Upscale-Kette. Der Worker liefert zwei deckungsgleiche Ebenen:
 *   customizationData.upscaledImage    = Detail-Pass (Final, nach der Überarbeitung)
 *   customizationData.upscaleRawImage  = Upscale vor dem Pass (gleiche Größe, gleiche Alpha-Maske)
 * und im Job-Dokument (upscaleJobs/{upscaleJobId}) den Report: Regionen mit Box, Score,
 * Hinweisen und dem tatsächlich überarbeiteten Rechteck.
 *
 * Dieses Mixin
 *   - abonniert den Job und stellt Regionen, Stil- und Inhaltsbefunde bereit,
 *   - bietet „Upscale" (vor dem Pass) als eigene Version in der Versionsleiste (UPSCALE_RAW),
 *   - regelt je Region die Deckkraft der Überarbeitung (0 % = dort bleibt der Upscale).
 *     Die VORSCHAU wird verkleinert im Browser zusammengesetzt (Millisekunden, kein PNG in
 *     voller Größe); erst der EXPORT setzt einmal in voller Auflösung zusammen. Kein Server-Aufruf.
 *   - speichert die Regler in der Konfiguration (`upscaleRegionOpacity`), jederzeit umkehrbar.
 *
 * Erwartet vom Editor: customizationData, selectedVariant, artworkBaseUrl, loadImage().
 */
import { jobRegions, scoreColor, subscribeUpscaleJob } from "@/upscaleJobs";
import { compositeUpscaleLayers } from "@/components/Upscales/compositeLayers";

const SHOW_REGIONS_KEY = "ohmyLiveEditorShowRegions";
/** Lange Kante der Vorschau — reicht für Editor und Lupe, der Export rechnet in voller Größe */
const PREVIEW_MAX_EDGE = 2400;

export const UPSCALE_RAW_VARIANT = "UPSCALE_RAW";

export default {
  data() {
    return {
      upscaleJob: null,
      upscaleJobError: "",
      upscaleJobUnsub: null,
      showUpscaleRegions: localStorage.getItem(SHOW_REGIONS_KEY) === "true",
      /** Reiter im Vergleichsdialog: 'overview' (Gesamtbild), 'faces' (alle Gesichter) oder die ID einer Region */
      compareTab: "overview",
      /** Gesichtsvergleich eng auf das Gesicht gezoomt (aus = ganzer Ausschnitt mit Umgebung) */
      compareFaceTight: true,
      /** Deckkraft der Überarbeitung je Region (id → 0…1); fehlt = 1 */
      upscaleRegionOpacity: {},
      hoveredUpscaleRegion: null,
      upscalePreviewUrl: null,
      upscalePreviewBusy: false,
      upscalePreviewError: "",
      upscalePreviewRun: 0,
      upscalePreviewTimer: null,
    };
  },
  computed: {
    upscaleJobIdOfTask() {
      return (this.customizationData && this.customizationData.upscaleJobId) || null;
    },
    upscaleScoreLimit() {
      return (this.upscaleJob && this.upscaleJob.taskScoreLimit) || 85;
    },
    upscaleRegions() {
      return jobRegions(this.upscaleJob);
    },
    /** Regionen samt aktuellem Regler-Stand (für Karten, Rahmen und Übersicht) */
    upscaleRegionCards() {
      return this.upscaleRegions.map((r) => {
        const adjustable = !!(r.replaced && r.rect);
        const opacity = adjustable ? this.regionOpacity(r.id) : 1;
        // überwiegend ausgeblendet → es gilt wieder der Score vor dem Pass
        const usesPass = adjustable && opacity >= 0.5;
        return { ...r, adjustable, opacity, currentScore: adjustable && !usesPass ? r.scoreBefore : r.score };
      });
    },
    /**
     * Neuester Upscale-Stand LIVE aus dem abonnierten Job (stages.final wird von Region-/Feedback-Edits fortgeschrieben).
     * Das Task-Objekt des Dialogs ist oft ein Schnappschuss vom Öffnen — ohne das hier zeigte „Edit 1" noch das alte Bild.
     */
    liveUpscaledImage() {
      const job = this.upscaleJob;
      const fromJob = job && job.status === "done" && job.stages && job.stages.final && job.stages.final.url;
      return fromJob || (this.customizationData && this.customizationData.upscaledImage) || null;
    },
    /** Untere Ebene (Upscale vor Detail-Pass bzw. Stand vor den Edits), ebenfalls live */
    liveUpscaleRawImage() {
      const cd = this.customizationData || {};
      if (cd.upscaleRawImage) return cd.upscaleRawImage;
      const job = this.upscaleJob;
      const edits = (job && job.edits) || [];
      const first = edits[0];
      return (first && first.previous && first.previous.final && first.previous.final.url) || null;
    },
    /** Beide Ebenen vorhanden und verschieden (es gab einen Detail-Pass oder einen Edit) */
    upscaleLayersAvailable() {
      const top = this.liveUpscaledImage;
      const raw = this.liveUpscaleRawImage;
      return !!(top && raw && raw !== top);
    },
    /** Regler nur für die neueste Version selbst — nicht für KI-Edits, Refinements oder das umgeschaltete 1024er-Original */
    upscaleLayersActive() {
      return this.upscaleLayersAvailable && this.selectedVariant === "DEFAULT" && this.artworkBaseUrl === this.liveUpscaledImage;
    },
    /** Regionen, in denen (anteilig) der Upscale durchscheinen soll */
    upscaleRegionMix() {
      return this.upscaleRegionCards.filter((r) => r.adjustable && r.opacity < 1).map((r) => ({ ...r.rect, amount: 1 - r.opacity, maskUrl: r.maskUrl || null }));
    },
    upscaleMixActive() {
      return this.upscaleLayersActive && this.upscaleRegionMix.length > 0;
    },
    /** Was der Editor anzeigt: die zusammengesetzte Vorschau, sobald ein Regler unter 100 % steht */
    artworkDisplayUrl() {
      return (this.upscaleMixActive && this.upscalePreviewUrl) || this.artworkBaseUrl;
    },
    /** Regionen für die Reiter des Vergleichsdialogs: Gesichter zuerst, sonst alle */
    compareFaceRegions() {
      const cards = this.upscaleRegionCards.filter((r) => r.crops && (r.crops.before || r.crops.after));
      const faces = cards.filter((r) => r.type === "face" || r.type === "animal_face");
      return [...faces, ...cards.filter((r) => !faces.includes(r))];
    },
    compareFacesLabel() {
      return this.compareFaceRegions.every((r) => r.type === "face" || r.type === "animal_face") ? "Alle Gesichter" : "Alle Regionen";
    },
    /** Im Gesichtsvergleich angezeigte Regionen (ein Reiter = eine Region, 'faces' = alle) */
    compareShownRegions() {
      if (this.compareTab === "overview") return [];
      const one = this.compareFaceRegions.filter((r) => r.id === this.compareTab);
      return one.length ? one : this.compareFaceRegions;
    },
    upscaleAnalysis() {
      return (this.upscaleJob && this.upscaleJob.report && this.upscaleJob.report.analysis) || null;
    },
    /** Rahmen über dem Artwork (Promille der Bildfläche → Prozent): Regionen + Inhaltsmeldungen */
    upscaleRegionBoxes() {
      const raw = this.selectedVariant === UPSCALE_RAW_VARIANT;
      if (this.selectedVariant !== "DEFAULT" && !raw) return [];
      const pct = (b) => ({ left: b.x / 10 + "%", top: b.y / 10 + "%", width: b.width / 10 + "%", height: b.height / 10 + "%" });
      const boxes = this.upscaleRegionCards
        .filter((r) => r.box)
        .map((r) => {
          const score = raw ? r.scoreBefore : r.currentScore;
          const color = scoreColor(score, this.upscaleScoreLimit);
          const show = (v) => (v === null || v === undefined ? "–" : String(v));
          // Überarbeitete Region mit sichtbarem Detail-Pass: Wert vorher → nachher
          const reworked = !raw && r.adjustable && r.opacity >= 0.5 && r.scoreBefore !== null && r.scoreBefore !== undefined && r.scoreBefore !== r.score;
          return {
            id: r.id,
            clickable: true,
            dashed: !raw && r.adjustable && r.opacity < 0.5,
            hovered: this.hoveredUpscaleRegion === r.id,
            color,
            text: reworked ? `${show(r.scoreBefore)} → ${show(r.score)}` : show(score),
            style: { ...pct(r.box), borderColor: color },
          };
        });
      const issues = ((this.upscaleAnalysis && this.upscaleAnalysis.contentIssues) || [])
        .filter((i) => i.boxResult && i.source === "detector")
        .map((i, index) => ({ id: "issue-" + index, issue: true, dashed: true, color: "#d8493f", text: "!", style: { ...pct(i.boxResult), borderColor: "#d8493f" } }));
      return [...boxes, ...issues];
    },
  },
  watch: {
    upscaleJobIdOfTask: { immediate: true, handler: "subscribeUpscaleJobOfTask" },
    showUpscaleRegions(value) {
      localStorage.setItem(SHOW_REGIONS_KEY, value ? "true" : "false");
    },
    upscaleRegionMix() {
      this.scheduleUpscalePreview();
    },
    upscaleLayersActive() {
      this.scheduleUpscalePreview();
    },
  },
  beforeDestroy() {
    if (this.upscaleJobUnsub) this.upscaleJobUnsub();
    clearTimeout(this.upscalePreviewTimer);
    this.setUpscalePreview(null);
    this.upscaleLayerImages = null;
    this.upscaleMaskImages = null;
  },
  methods: {
    subscribeUpscaleJobOfTask() {
      if (this.upscaleJobUnsub) this.upscaleJobUnsub();
      this.upscaleJobUnsub = null;
      this.upscaleJob = null;
      this.upscaleJobError = "";
      this.upscaleLayerImages = null;
      if (!this.upscaleJobIdOfTask) return;
      this.upscaleJobUnsub = subscribeUpscaleJob(
        this.upscaleJobIdOfTask,
        (job) => {
          this.upscaleJob = job;
        },
        (err) => {
          this.upscaleJobError = err.code === "permission-denied" ? "Kein Zugriff auf upscaleJobs (Firestore-Regel fehlt)" : err.message;
        }
      );
    },

    /**
     * Vergleichsdialog direkt im Gesichtsvergleich öffnen: für eine Region (Klick auf den
     * Rahmen im Bild oder das Vorschaubild der Karte) oder für alle Gesichter.
     */
    openUpscaleFace(id) {
      this.resetCompareView();
      this.compareTab = id && this.upscaleRegionCards.some((r) => r.id === id) ? id : "faces";
      this.compareDialog = true;
    },

    regionOpacity(id) {
      const value = this.upscaleRegionOpacity[id];
      return typeof value === "number" ? Math.max(0, Math.min(1, value)) : 1;
    },

    /** Deckkraft der Überarbeitung einer Region setzen (0…1) */
    setUpscaleRegionOpacity(id, value) {
      const next = { ...this.upscaleRegionOpacity };
      const v = Math.max(0, Math.min(1, Number(value)));
      if (v >= 1) delete next[id];
      else next[id] = v;
      this.upscaleRegionOpacity = next;
    },

    /** Regler aus der gespeicherten Konfiguration wiederherstellen (inkl. älterem Format „verworfene Regionen") */
    restoreUpscaleRegionOpacity(config) {
      const next = {};
      const saved = (config && config.upscaleRegionOpacity) || {};
      for (const [id, v] of Object.entries(saved)) if (typeof v === "number" && v < 1) next[id] = Math.max(0, v);
      for (const id of (config && config.upscaleHiddenRegions) || []) if (!(id in next)) next[id] = 0;
      this.upscaleRegionOpacity = next;
    },

    /** Beide Ebenen einmal laden und behalten (nicht reaktiv — große Bilder) */
    loadUpscaleLayerImages() {
      const cd = this.customizationData;
      const key = cd.upscaledImage + "|" + cd.upscaleRawImage;
      if (!this.upscaleLayerImages || this.upscaleLayerImages.key !== key) {
        const promise = Promise.all([this.loadImage(cd.upscaledImage), this.loadImage(cd.upscaleRawImage)]);
        this.upscaleLayerImages = { key, promise };
        promise.catch(() => {
          if (this.upscaleLayerImages && this.upscaleLayerImages.key === key) this.upscaleLayerImages = null;
        });
      }
      return this.upscaleLayerImages.promise;
    },

    /** Einsetz-Masken der geregelten Regionen laden (je URL einmal) und an die Regionen hängen */
    async withRegionMasks(regions) {
      if (!this.upscaleMaskImages) this.upscaleMaskImages = {};
      return Promise.all(
        regions.map(async (r) => {
          if (!r.maskUrl) return r;
          if (!this.upscaleMaskImages[r.maskUrl]) this.upscaleMaskImages[r.maskUrl] = this.loadImage(r.maskUrl);
          try {
            return { ...r, maskImg: await this.upscaleMaskImages[r.maskUrl] };
          } catch (e) {
            delete this.upscaleMaskImages[r.maskUrl];
            return r; // ohne Maske: ganzes Rechteck
          }
        })
      );
    },

    upscaleRefSize() {
      const stages = (this.upscaleJob && this.upscaleJob.stages) || {};
      // Die Rechtecke beziehen sich auf die Größe des Bildes im Detail-Pass (= Upscale)
      return stages.upscale && stages.upscale.width ? { width: stages.upscale.width, height: stages.upscale.height } : null;
    },

    setUpscalePreview(url) {
      if (this.upscalePreviewUrl && this.upscalePreviewUrl !== url) {
        try {
          URL.revokeObjectURL(this.upscalePreviewUrl);
        } catch (e) {
          /* schon freigegeben */
        }
      }
      this.upscalePreviewUrl = url;
    },

    scheduleUpscalePreview() {
      clearTimeout(this.upscalePreviewTimer);
      this.upscalePreviewTimer = setTimeout(() => this.rebuildUpscalePreview(), 60);
    },

    /** Verkleinerte Vorschau zusammensetzen — schnell genug, um dem Regler zu folgen */
    async rebuildUpscalePreview() {
      const run = ++this.upscalePreviewRun;
      if (!this.upscaleMixActive) {
        this.setUpscalePreview(null);
        this.upscalePreviewBusy = false;
        this.upscalePreviewError = "";
        return;
      }
      this.upscalePreviewBusy = true;
      this.upscalePreviewError = "";
      try {
        const [[finalImg, rawImg], regions] = await Promise.all([this.loadUpscaleLayerImages(), this.withRegionMasks(this.upscaleRegionMix)]);
        if (run !== this.upscalePreviewRun) return;
        const canvas = compositeUpscaleLayers(finalImg, rawImg, regions, { refSize: this.upscaleRefSize(), maxEdge: PREVIEW_MAX_EDGE });
        // Transparente Artworks brauchen Alpha (PNG), sonst reicht ein schnelles JPEG
        const transparent = !!(this.customizationData.upscale && this.customizationData.upscale.transparent);
        const blob = await new Promise((resolve, reject) =>
          canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Vorschau konnte nicht erzeugt werden"))), transparent ? "image/png" : "image/jpeg", 0.92)
        );
        if (run !== this.upscalePreviewRun) return;
        this.setUpscalePreview(URL.createObjectURL(blob));
      } catch (err) {
        if (run !== this.upscalePreviewRun) return;
        console.error("Upscale-Ebenen:", err);
        this.setUpscalePreview(null);
        this.upscalePreviewError = "Vorschau der Regionen-Regler fehlgeschlagen — angezeigt wird der Detail-Pass. " + ((err && err.message) || "");
      } finally {
        if (run === this.upscalePreviewRun) this.upscalePreviewBusy = false;
      }
    },

    /**
     * Bildquelle für den Export: mit Reglern unter 100 % das in VOLLER Auflösung
     * zusammengesetzte Canvas, sonst das Bild selbst.
     */
    async exportArtworkSource(url) {
      if (!this.upscaleMixActive) return this.loadImage(url);
      const [[finalImg, rawImg], regions] = await Promise.all([this.loadUpscaleLayerImages(), this.withRegionMasks(this.upscaleRegionMix)]);
      return compositeUpscaleLayers(finalImg, rawImg, regions, { refSize: this.upscaleRefSize() });
    },
  },
};
